import { matchGlobalSearchQuery, type GlobalSearchMatch } from "./globalSearchMatch";
import { GLOBAL_SEARCH_RESULTS_PATH, type GlobalSearchDestination } from "./globalSearchDestinations";
import {
  globalSearchDestinationIsAvailable,
  globalSearchDestinationIsInContext,
  type GlobalSearchResolveContext
} from "./globalSearchResolve";

export type RankedGlobalSearchDestination = GlobalSearchDestination & {
  match: GlobalSearchMatch;
  catalogIndex: number;
};

const PINNED_SEARCH_RESULTS_MATCH: GlobalSearchMatch = {
  score: -1,
  labelIndices: [],
  breadcrumbIndices: []
};

export function isPinnedGlobalSearchResultsDestination(
  destination: RankedGlobalSearchDestination | undefined
): boolean {
  return Boolean(destination && destination.path === GLOBAL_SEARCH_RESULTS_PATH && destination.match.score < 0);
}

export function rankGlobalSearchDestinations(
  destinations: GlobalSearchDestination[],
  query: string,
  context: GlobalSearchResolveContext = {}
): RankedGlobalSearchDestination[] {
  const trimmed = query.trim();

  const ranked = destinations.flatMap((destination, catalogIndex) => {
    if (!globalSearchDestinationIsAvailable(destination.path, context)) return [];
    const match = matchGlobalSearchQuery(trimmed, destination.label, destination.breadcrumbs, destination.keywords);
    if (destination.path === GLOBAL_SEARCH_RESULTS_PATH && trimmed) {
      return [{ ...destination, match: match ?? PINNED_SEARCH_RESULTS_MATCH, catalogIndex }];
    }
    if (!match) return [];
    return [{ ...destination, match, catalogIndex }];
  });

  ranked.sort((a, b) => {
    if (trimmed) {
      if (b.match.score !== a.match.score) return b.match.score - a.match.score;
      const contextA = globalSearchDestinationIsInContext(a.path, context) ? 1 : 0;
      const contextB = globalSearchDestinationIsInContext(b.path, context) ? 1 : 0;
      if (contextB !== contextA) return contextB - contextA;
    }

    const depthA = a.breadcrumbs.length;
    const depthB = b.breadcrumbs.length;
    if (depthA !== depthB) return depthA - depthB;
    return a.catalogIndex - b.catalogIndex;
  });

  return ranked;
}
